import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  useTheme,
  Tabs,
  Tab,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import {
  PhoneIphoneOutlined,
  AccountBalanceOutlined,
  CheckCircleOutline,
  SendOutlined,
  ContentCopyOutlined,
  InfoOutlined,
  DoneAllOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { mpesaPaySGW } from "../../data/DynamicTable/DynamicFunctions";
import Swal from "sweetalert2";
import { formatKES } from "./WalletBalance";

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000, 100000, 250000];

const WalletTopUp = ({
  companyId,
  userPhone = "0712345678",
  onTopUpSuccess,
  paybillNumber = "9617815",
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [activeTab, setActiveTab] = useState(0); // 0: M-Pesa STK, 1: Paybill / Bank
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("0712345678");
  const [loading, setLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  // STK Push awaiting prompt state
  const [stkState, setStkState] = useState({
    sent: false,
    checkoutRequestId: "",
    message: "",
    targetPhone: "",
    targetAmount: 0,
  });

  // Standardize Kenyan phone number to 254XXXXXXXXX
  const normalizePhoneNumber = (input) => {
    let clean = input.replace(/\D/g, "");
    if (clean.startsWith("0")) {
      clean = "254" + clean.substring(1);
    } else if (clean.startsWith("7") || clean.startsWith("1")) {
      clean = "254" + clean;
    }
    return clean;
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(""), 2500);
  };

  const handleSTKSubmit = async (e) => {
    if (e) e.preventDefault();

    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount < 10) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter an amount of at least KES 10.",
      });
      return;
    }

    if (numericAmount > 300000) {
      Swal.fire({
        icon: "warning",
        title: "Exceeds M-Pesa Limit",
        text: "Single STK push transactions cannot exceed KES 300,000. For larger amounts, please use the Bank Transfer tab.",
      });
      return;
    }

    const cleanPhone = normalizePhoneNumber(phoneNumber);
    if (cleanPhone.length !== 12 || !cleanPhone.startsWith("254")) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Please enter a valid Kenyan Safaricom phone number (e.g. 0712345678 or 254712345678).",
      });
      return;
    }

    setLoading(true);

    try {
      // Initiate STK push (account SGW_<companyId>); the wallet is credited
      // by the M-Pesa callback once the customer confirms on their handset.
      const response = await mpesaPaySGW(numericAmount, cleanPhone, companyId);

      // mpesaPaySGW resolves to an error code string when the request fails
      if (!response || typeof response === "string" || response.status >= 400) {
        throw new Error(
          typeof response === "string"
            ? `Request failed (${response})`
            : "Could not initiate M-Pesa STK push."
        );
      }

      const checkoutRequestId =
        response.data?.checkoutRequestId ||
        response.data?.CheckoutRequestID ||
        `ws_CO_${Date.now()}`;

      setStkState({
        sent: true,
        checkoutRequestId,
        message:
          response.data?.message ||
          "STK push sent. Enter M-Pesa PIN on handset to complete top-up.",
        targetPhone: cleanPhone,
        targetAmount: numericAmount,
      });

      Swal.fire({
        icon: "success",
        title: "STK Push Initiated",
        text: `A payment prompt of ${formatKES(numericAmount)} was sent to ${cleanPhone}. Please enter your M-Pesa PIN.`,
        timer: 3500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Top-up request failed:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.response?.data ||
        err.message ||
        "Could not initiate M-Pesa STK push. Please verify your details and try again.";
      Swal.fire({
        icon: "error",
        title: "Top-up Failed",
        text: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStkState({
      sent: false,
      checkoutRequestId: "",
      message: "",
      targetPhone: "",
      targetAmount: 0,
    });
    setAmount("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Top Selector Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
          },
        }}
      >
        <Tab
          icon={<PhoneIphoneOutlined />}
          iconPosition="start"
          label="M-Pesa STK Push (Instant)"
        />
        <Tab
          icon={<AccountBalanceOutlined />}
          iconPosition="start"
          label="Paybill & Bank Deposits (Clears in two days)"
        />
      </Tabs>

      {/* TAB 0: M-PESA STK PUSH */}
      {activeTab === 0 && (
        <Box>
          {!stkState.sent ? (
            <Card
              sx={{
                borderRadius: 2.5,
                backgroundColor: isDark ? colors.primary[400] : "#ffffff",
                boxShadow: "none",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                    Deposit Funds via M-Pesa STK Push
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.5 }}>
                    Enter your deposit amount and Safaricom phone number. A secure PIN prompt will appear instantly on your phone.
                  </Typography>
                </Box>

                {/* Quick Amount Chips */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ color: colors.grey[300], fontWeight: 600, display: "block", mb: 1 }}>
                    SELECT PRESET AMOUNT (KES)
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {QUICK_AMOUNTS.map((amt) => {
                      const isSelected = parseFloat(amount) === amt;
                      return (
                        <Chip
                          key={amt}
                          label={`+KES ${amt.toLocaleString()}`}
                          clickable
                          onClick={() => setAmount(String(amt))}
                          variant={isSelected ? "filled" : "outlined"}
                          color={isSelected ? "primary" : "default"}
                          sx={{
                            fontWeight: isSelected ? 700 : 500,
                            borderRadius: 1.5,
                            borderWidth: isSelected ? 2 : 1,
                            backgroundColor: isSelected
                              ? "#1766A2"
                              : isDark
                              ? "rgba(255,255,255,0.04)"
                              : "transparent",
                            color: isSelected ? "#fff" : colors.grey[200],
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "#1766A2"
                                : isDark
                                ? "rgba(255,255,255,0.1)"
                                : "#f1f5f9",
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                {/* Inputs Grid */}
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                      Amount (KES) *
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      placeholder="e.g. 50000"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: colors.grey[400] }}>
                              KES
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                      M-Pesa Phone Number *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. 0712345678 or 254712345678"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIphoneOutlined sx={{ color: colors.grey[400] }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Calculation Summary Box */}
                {parseFloat(amount) > 0 && (
                  <Paper
                    sx={{
                      p: 2,
                      mt: 3,
                      borderRadius: 2,
                      backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Deposit Amount
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          {formatKES(amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Top-Up Processing Fee
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#22bf72" }}>
                          KES 0.00 (Free)
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          Net Wallet Credit
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1f76ad" }}>
                          {formatKES(amount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Inflow Waterfall Architecture Note (Section 8.2) */}
                <Paper
                  sx={{
                    p: 2,
                    mt: 2,
                    borderRadius: 2,
                    backgroundColor: isDark ? "rgba(23,102,162,0.1)" : "#f0f7fc",
                    border: "1px solid rgba(23,102,162,0.2)",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1766a2", display: "flex", alignItems: "center", gap: 0.5 }}>
                    <InfoOutlined sx={{ fontSize: 14 }} />
                    Inflow Waterfall Priority (Section 8.2 & 10.4):
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mt: 0.5 }}>
                    Top-up deposits automatically settle in order: (1) accrued overdraft interest, (2) overdraft principal, and (3) remainder credits available wallet balance.
                  </Typography>
                </Paper>

                {/* Submit Action */}
                <Box sx={{ mt: 3.5, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                  {onClose && (
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSTKSubmit}
                    disabled={loading || !amount}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendOutlined />}
                    sx={{
                      backgroundColor: "#22bf72",
                      color: "#fff",
                      px: 4,
                      py: 1.2,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                      "&:hover": { backgroundColor: "#1b995b" },
                    }}
                  >
                    {loading ? "Sending Prompt..." : "Send M-Pesa STK Push"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            /* STK Prompt Active State */
            <Card
              sx={{
                borderRadius: 2.5,
                backgroundColor: isDark ? colors.primary[400] : "#ffffff",
                boxShadow: "none",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                textAlign: "center",
                p: { xs: 3, sm: 5 },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgba(34, 191, 114, 0.12)",
                  color: "#22bf72",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                  animation: "pulse 2s infinite ease-in-out",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(34, 191, 114, 0.4)" },
                    "70%": { transform: "scale(1.05)", boxShadow: "0 0 0 16px rgba(34, 191, 114, 0)" },
                    "100%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(34, 191, 114, 0)" },
                  },
                }}
              >
                <PhoneIphoneOutlined sx={{ fontSize: 44 }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1 }}>
                M-Pesa PIN Prompt Dispatched!
              </Typography>
              <Typography variant="body1" sx={{ color: colors.grey[300], maxWidth: 500, mx: "auto", mb: 3 }}>
                A payment request for <strong>{formatKES(stkState.targetAmount)}</strong> has been sent to{" "}
                <strong>{stkState.targetPhone}</strong>.
              </Typography>

              <Alert severity="info" sx={{ maxWidth: 520, mx: "auto", mb: 3, textAlign: "left" }}>
                1. Unlock your mobile phone.<br />
                2. Enter your M-Pesa PIN when prompted.<br />
                3. Once confirmed, Safaricom sends a callback and your wallet balance updates immediately.
              </Alert>

              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 3 }}>
                Request Reference: <code>{stkState.checkoutRequestId}</code>
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Start New Top-Up
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (onTopUpSuccess) onTopUpSuccess();
                    if (onClose) onClose();
                  }}
                  startIcon={<RefreshOutlined />}
                  sx={{
                    backgroundColor: "#1766A2",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Verify & Refresh Balance
                </Button>
              </Stack>
            </Card>
          )}
        </Box>
      )}

      {/* TAB 1: PAYBILL & MANUAL BANK DEPOSIT */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Direct Paybill Instructions */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2.5,
                backgroundColor: isDark ? colors.primary[400] : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "rgba(34, 191, 114, 0.15)",
                      color: "#22bf72",
                      display: "flex",
                    }}
                  >
                    <PhoneIphoneOutlined />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                    Direct M-Pesa Paybill
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2.5 }}>
                  You can pay directly from your Safaricom SIM Toolkit or M-Pesa app using our shared Global Paybill shortcode.
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        BUSINESS / PAYBILL NUMBER
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                        {paybillNumber}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      startIcon={copiedKey === "paybill" ? <DoneAllOutlined /> : <ContentCopyOutlined />}
                      onClick={() => handleCopy(paybillNumber, "paybill")}
                      sx={{ textTransform: "none", fontSize: "0.75rem" }}
                    >
                      {copiedKey === "paybill" ? "Copied" : "Copy"}
                    </Button>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        ACCOUNT NUMBER
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#1f76ad" }}>
                        {`TOPUP-${String(companyId).padStart(4, "0")}`}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      startIcon={copiedKey === "account" ? <DoneAllOutlined /> : <ContentCopyOutlined />}
                      onClick={() =>
                        handleCopy(`TOPUP-${String(companyId).padStart(4, "0")}`, "account")
                      }
                      sx={{ textTransform: "none", fontSize: "0.75rem" }}
                    >
                      {copiedKey === "account" ? "Copied" : "Copy"}
                    </Button>
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
                  * The account number format ensures instant routing and attribution to your specific company ledger.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Institutional Bank Transfer */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2.5,
                backgroundColor: isDark ? colors.primary[400] : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "rgba(31, 118, 173, 0.15)",
                      color: "#1f76ad",
                      display: "flex",
                    }}
                  >
                    <AccountBalanceOutlined />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                    Manual Bank Transfer (Large Volumes)
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
                  For institutional funding exceeding M-Pesa limits (e.g. above KES 300,000), wire funds directly to our central settlement escrow account.
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
                    fontSize: "0.85rem",
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Bank:</strong> Standard Chartered Bank Kenya
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Branch:</strong> Westlands Branch
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Account Name:</strong> Sababisha Solutions Limited - Escrow
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Account Number:</strong> 0108098234100
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#1f76ad", fontWeight: 700 }}>
                    <strong>Payment Reference:</strong> SAB-CO-{String(companyId).padStart(4, "0")}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <InfoOutlined sx={{ fontSize: 16, color: colors.grey[400], mt: 0.2 }} />
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    Once transferred, notify Sababisha Finance Support. Upon verification, funds are posted as a <code>TOPUP_MANUAL</code> ledger credit.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default WalletTopUp;
